import { z } from 'zod';
import { reelCoverInputSchema } from '../reels/storyboard.js';
import { id, ifMatch } from './tool-base.js';
const rightsConfirmed = z
    .literal(true)
    .describe('true only when the human told you that they hold the rights to the picture and the music of this video. Recorded under their name.');
const cover = reelCoverInputSchema.optional().describe('Cover of the reel; default: the first frame.');
/**
 * Scope `media` (ADR-040): bring a file into the media library and attach a finished video to a reel.
 * The file itself never passes through the MCP server: the client PUTs it to the signed upload URL.
 */
export const MEDIA_TOOLS = [
    {
        name: 'create_media_upload',
        title: 'Start a media upload',
        description: 'Step 1 of 2. Returns upload_url, upload_method and upload_headers, valid for a few minutes. Send the file yourself to upload_url with exactly that method and those headers (e.g. curl -X PUT -H … --data-binary @file), then call finalize_media_upload. Photos, videos (mp4, mov) and audio; size_bytes is the exact file size.',
        operationId: 'createMediaUpload',
        scope: 'media',
        method: 'POST',
        path: '/v1/media-assets/uploads',
        input: z.object({
            property_id: id,
            filename: z.string().min(1).max(255).describe('File name with extension, e.g. reel-ordination.mp4; becomes the title.'),
            mime_type: z.string().min(3).max(100).describe('e.g. video/mp4, image/jpeg.'),
            size_bytes: z.number().int().min(1),
        }),
    },
    {
        name: 'finalize_media_upload',
        title: 'Finish a media upload',
        description: 'Step 2 of 2, after the file was sent to upload_url. Checks the stored file and creates the media asset (it starts in processing; no AI tag suggestions, tags stay with a human). The same file uploaded twice returns the existing asset with exact_duplicate: true.',
        operationId: 'finalizeMediaUpload',
        scope: 'media',
        method: 'POST',
        path: '/v1/media-assets',
        input: z.object({
            upload_id: id,
            property_id: id.optional(),
            people_visible: z.boolean().optional().describe('Whether people can be recognised; default true.'),
            social_release: z
                .boolean()
                .optional()
                .describe('true only when the human told you that everyone recognisable agreed to social media use; default false. Recorded under their name.'),
            season: z.enum(['spring', 'summer', 'autumn', 'winter', 'all_year']).nullable().optional(),
            pillar_id: id.nullable().optional(),
            entry_ids: z.array(id).max(20).optional().describe('Entries (list_entries) shown in the file.'),
            location: z.string().max(200).nullable().optional(),
        }),
    },
    {
        name: 'attach_reel_video',
        title: 'Attach a finished video to a reel',
        description: 'Makes an uploaded video (a media asset of kind video, finalize_media_upload) the finished video of a reel or video story format that has no storyboard yet; a linked Facebook reel follows. if_match is the etag of get_content_item. Use replace_reel_video when get_storyboard finds one.',
        operationId: 'createVariantStoryboard',
        scope: 'media',
        method: 'POST',
        path: '/v1/content-variants/{variant_id}/storyboard',
        input: z.object({ variant_id: id, if_match: ifMatch, uploaded_asset_id: id, rights_confirmed: rightsConfirmed, cover }),
        fixed: { mode: 'uploaded' },
    },
    {
        name: 'replace_reel_video',
        title: 'Replace the video of a reel',
        description: 'Replaces the storyboard or video of a reel (storyboard_id and etag from get_storyboard) with an uploaded video. Every change is a new version; an approved post goes back to draft.',
        operationId: 'replaceStoryboard',
        scope: 'media',
        method: 'PUT',
        path: '/v1/storyboards/{storyboard_id}',
        input: z.object({ storyboard_id: id, if_match: ifMatch, uploaded_asset_id: id, rights_confirmed: rightsConfirmed, cover }),
        fixed: { mode: 'uploaded' },
    },
];
//# sourceMappingURL=media-tools.js.map